const fallbackSymbol = Symbol('nullish');

/**
 * Memoizes a function with weak maps with up to 4 arguments.
 * Uses a fallback symbol for null or undefined arguments.
 */
export const memoizeWeak = <T extends (...args: any[]) => any>(fn: T): T => {
  const cache = new WeakMap<
    object,
    WeakMap<object, WeakMap<object, WeakMap<object, ReturnType<T>>>>
  >();
  return ((
    arg1: any = fallbackSymbol,
    arg2: any = fallbackSymbol,
    arg3: any = fallbackSymbol,
    arg4: any = fallbackSymbol
  ) => {
    const key1 = arg1 ?? fallbackSymbol;
    const key2 = arg2 ?? fallbackSymbol;
    const key3 = arg3 ?? fallbackSymbol;
    const key4 = arg4 ?? fallbackSymbol;

    if (!cache.has(key1)) {
      cache.set(key1, new WeakMap());
    }
    const arg1Cache = cache.get(key1)!;
    if (!arg1Cache.has(key2)) {
      arg1Cache.set(key2, new WeakMap());
    }
    const arg2Cache = arg1Cache.get(key2)!;
    if (!arg2Cache.has(key3)) {
      arg2Cache.set(key3, new WeakMap());
    }
    const arg3Cache = arg2Cache.get(key3)!;
    if (!arg3Cache.has(key4)) {
      arg3Cache.set(key4, fn(arg1, arg2, arg3, arg4));
    }
    return arg3Cache.get(key4);
  }) as T;
};
